import React, { useContext } from 'react';
import {
  configureAiHubLibrary,
  WsAiHubPage,
} from '@ava-innersource/wsp-ai-hub-library';

import UserProvider from '../../UserProvider';
import UserContext from '../../UserContext';
import { httpInstance } from '../../core';
import { wsSocketClient } from '../../socket-client';

import '../Deliver/Pages/Page.scss';

// AI Hub backend runs separately from the main deliver portal API. In dev
// the deliver portal API listens on :4000 (REACT_APP_WSP_API_BASE_URL) while
// AI Hub listens on :5000 (REACT_APP_AI_HUB_API_BASE_URL). All AI Hub
// library calls must therefore be retargeted to the AI Hub host instead of
// going through the standard `helpers-sales` apiGet/apiPost/apiPut helpers
// (which hard-prefix the deliver portal base URL).
const AI_HUB_API_BASE_URL =
  process.env.REACT_APP_AI_HUB_API_BASE_URL ||
  process.env.REACT_APP_WSP_API_BASE_URL ||
  '';

const buildAiHubUrl = (path: string): string =>
  /^https?:/i.test(path) ? path : `${AI_HUB_API_BASE_URL}/api${path}`;

const aiHubApiGet = async (url: string): Promise<any> => {
  try {
    const response = await httpInstance.get(buildAiHubUrl(url));
    return response.data ?? [];
  } catch (error) {
    console.error('AI Hub GET error:', error);
    return [];
  }
};

const aiHubApiPost = async (
  url: string,
  data?: any,
  headers?: any,
): Promise<any> => {
  try {
    const response = await httpInstance.post(buildAiHubUrl(url), data, {
      headers,
    });
    return response.data ?? [];
  } catch (error) {
    console.error('AI Hub POST error:', error);
    return [];
  }
};

const aiHubApiPut = async (
  url: string,
  data?: any,
  headers?: any,
): Promise<any> => {
  try {
    const response = await httpInstance.put(buildAiHubUrl(url), data, {
      headers,
    });
    return response.data ?? [];
  } catch (error) {
    console.error('AI Hub PUT error:', error);
    return [];
  }
};

// Configure the AI Hub library once at module load with portal-specific
// dependencies. Doing this in the render body would re-run on every render.
configureAiHubLibrary({
  apiGet: aiHubApiGet,
  apiPost: aiHubApiPost,
  apiPut: aiHubApiPut,
  socketClient: wsSocketClient,
});

/**
 * Portal-side adapter: ensures a `UserContext` is available, then renders
 * the framework-agnostic `WsAiHubPage` from the AI Hub library, passing the
 * authenticated user down so the admin section can attribute actions.
 */
const AiHubPageInner = (): JSX.Element => {
  const userContext = useContext(UserContext);
  const email = userContext?.currentUser?.email || '';

  return <WsAiHubPage currentUser={{ email }} />;
};

const WsAiHubPage_Portal = (): JSX.Element => (
  <UserProvider>
    <AiHubPageInner />
  </UserProvider>
);

export { WsAiHubPage_Portal as WsAiHubPage };
